import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { SubscriptionState, DEFAULT_SUBSCRIPTION_STATE, PRODUCT_TO_TIER, FOUNDING_OFFER_EXPIRES, cacheSubscriptionState } from '@/lib/subscription';

export function useSubscription() {
  const [state, setState] = useState<SubscriptionState>(DEFAULT_SUBSCRIPTION_STATE);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const checkSubscription = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setState({ ...DEFAULT_SUBSCRIPTION_STATE, loading: false });
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription');
      if (error) throw error;

      const productId = data.product_id ?? null;
      const tier = productId ? (PRODUCT_TO_TIER[productId] ?? null) : null;
      const newState: SubscriptionState = {
        subscribed: data.subscribed ?? false,
        productId,
        subscriptionEnd: data.subscription_end ?? null,
        loading: false,
        tier,
        isFoundingMember: tier === 'founding_member',
        foundingOfferActive: data.founding_offer_active ?? (new Date() < FOUNDING_OFFER_EXPIRES),
      };
      setState(newState);
      cacheSubscriptionState(newState);
    } catch (err) {
      console.error('Failed to check subscription:', err);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    checkSubscription();
    const interval = setInterval(checkSubscription, 60000);

    // Re-check when auth state changes (e.g. after background sign-in with PIN)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // Small delay to ensure session is fully established
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = setTimeout(checkSubscription, 500);
      }
    });

    return () => {
      clearInterval(interval);
      clearTimeout(retryTimeoutRef.current);
      subscription.unsubscribe();
    };
  }, [checkSubscription]);

  const startCheckout = useCallback(async (priceId: string, mode: 'subscription' | 'payment' = 'subscription') => {
    // Refresh session to ensure a valid token before checkout
    const { error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) throw new Error('Session expired. Please log in again.');

    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: { priceId, mode },
    });
    if (error) throw error;
    if (data?.url) {
      window.location.href = data.url;
    }
  }, []);

  const openPortal = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke('customer-portal');
    if (error) throw error;
    if (data?.url) {
      window.location.href = data.url;
    }
  }, []);

  return { ...state, checkSubscription, startCheckout, openPortal };
}
